import {
    DialogContent,
    TextField,
    Button,
    useTheme,
    Input,
} from "@mui/material";
import { useState } from "react";
import { useErrorDisplay } from "../components/ErrorDisplay"
import { GetUpsertIcon, IsEmpty } from "../Helpers";
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { UpsertAction } from "../Enums";
import MarketplaceClient, { MarketplaceErrors, UploadTemplateContext } from "./MarketplaceClient";
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";
import useViewportMode from "../hooks/useViewportMode";

export interface TemplateUploadUpsertProps extends Omit<IconButtonExProps, 'onClick'> {
    mode: UpsertAction;
    templateRevisionId: string;
    onChange?: () => Promise<void>;
    onAdd?: (id: string) => Promise<void>;
    icon?: JSX.Element;
}

const TemplateUploadUpsertPopup = ({ mode, templateRevisionId, onChange = async () => { }, onAdd = async () => { }, icon = GetUpsertIcon(mode), ...btnProps }: TemplateUploadUpsertProps) => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();
    const viewport = useViewportMode();

    const emptyContext: UploadTemplateContext = {
        ShortDescription: '',
        DetailedDescription: '',
        Tags: '',
        Price: 0,
        TemplateRevisionId: ''
    };

    const [uploadContext, setUploadContext] = useState<UploadTemplateContext>(emptyContext);

    const disableSave = IsEmpty(uploadContext.ShortDescription);

    const title = (mode === UpsertAction.Create) ? 'Upload Template to Marketplace' : `Edit Marketplace Template`;

    const handleSubmit = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                await new MarketplaceClient().Upload(uploadContext);
                setOpen(false);
            } else {
                setError(new Error('Updating Template Metadata is not supported at this time.'), MarketplaceErrors.Upload);
            }

        } catch (error) {
            setError(error, (mode === UpsertAction.Create) ? MarketplaceErrors.Upload : MarketplaceErrors.Upload);
        }
        finally {
            setBusy(false);
        }
    };

    const handleInputChange = e => {
        const { name, value } = e.target
        setUploadContext({ ...uploadContext, [name]: value })
    };

    const handleOpen = async () => {
        setOpen(true);
        await loadData();
    };

    const loadData = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                setUploadContext({ ...emptyContext, TemplateRevisionId: templateRevisionId });
            } else {
                setError(new Error('Updating Template Metadata is not supported at this time.'), MarketplaceErrors.Upload);
            }

        } catch (error) {
            setError(error, MarketplaceErrors.Upload);
        }
        finally {
            setBusy(false);
        }
    };

    return <>
        <ErrorDisplay />
        <IconButtonEx disabled={busy} hoverContent={title} size='small' onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

        <ResponsiveDialog
            aria-labelledby="customized-dialog-title"
            open={open}
            fullWidth
            maxWidth="sm"
            tabletFullScreen="portrait"
            PaperProps={{ sx: { '& > .responsive-dialog-busy': { display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' } } }}
        >
            <BusySpinner busy={busy} className="responsive-dialog-busy">
                <ResponsiveDialogHeader id="customized-dialog-title" title={title} onClose={() => setOpen(false)} />
                <DialogContent dividers sx={{ p: viewport.isPhone ? 1.5 : theme.spacing(2), minHeight: 0, overflowY: 'auto' }}>
                    <TextField label="Short Description" fullWidth autoFocus
                        name="ShortDescription"
                        sx={{ marginBottom: theme.spacing(1) }}
                        error={IsEmpty(uploadContext.ShortDescription)}
                        size='small'
                        variant="outlined"
                        value={uploadContext.ShortDescription}
                        onChange={handleInputChange} />
                    <TextField label="Detailed Description" fullWidth
                        name="DetailedDescription"
                        sx={{ marginBottom: theme.spacing(1) }}
                        error={IsEmpty(uploadContext.DetailedDescription)}
                        size='small'
                        variant="outlined"
                        value={uploadContext.DetailedDescription}
                        onChange={handleInputChange} />
                    <TextField label="Tags" fullWidth
                        name="Tags"
                        sx={{ marginBottom: theme.spacing(1) }}
                        error={IsEmpty(uploadContext.Tags)}
                        size='small'
                        variant="outlined"
                        value={uploadContext.Tags}
                        onChange={handleInputChange} />
                    <Input name="Price" fullWidth
                        sx={{ marginBottom: theme.spacing(1) }}
                        type="number"
                        inputProps={{ 'aria-label': 'Price' }}
                        value={uploadContext.Price ?? 0.00}
                        onChange={handleInputChange} />
                </DialogContent>
                <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                    <Button onClick={() => setOpen(false)} variant='text' color='inherit'>Cancel</Button>
                    <Button disabled={disableSave} onClick={handleSubmit} variant='outlined' color='primary'>Upload</Button>
                </StickyDialogActions>
            </BusySpinner>
        </ResponsiveDialog>
    </>;
}

export default TemplateUploadUpsertPopup;
